// ============================================================================
// layer.housings.detail — the layer housing record page
// ============================================================================
// The same move as its batch sibling, and the same two resources: the batch and
// housing directories plus the two metric feeds in layer.record.core, the
// ppp-layer-groups-v1 blob in layer.record.appStore. Both used to arrive as
// LayerEntry props off App's boot, and the metric feeds were paginated by this
// page through a `fetchAll(table, columns)` helper whose table was a PARAMETER
// — so both of its reads inventoried as `from:~table` and could prove ownership
// of neither literal table.
//
// It shares its siblings' resources because LayerBatchesRouter chooses which of
// the two record pages renders and both need the same reads; the branch is
// what makes them two surfaces, not the data.
//
// PERMANENT since the boot release. Its batch sibling is still PARTIAL, and the
// difference between them is one prop, so the paragraphs below say what the
// residual WAS and why removing it had to land with the release itself.
//
// ── WHAT THE RESIDUAL WAS, AND WHAT IT WAS NOT ──────────────────────────────
//
// It was NOT the database write. `sb.from('layer_housings').upsert(…)` was
// already page-local, in both `persistHousing` and `retireNow`, and it has not
// moved. What was App's is the POST-write fan-out: `persistLayerHousings` is
// only `setLayerHousings(nb)` plus `triggerMaintenance({layerHousings: nb, …})`.
// It performs no write of its own — note it has no `sbSave`, unlike its
// `persistLayerGroups` sibling. (Since migration 242 (R4) both writes are
// housing.update calls to layer_apply_operation through
// src/layer/layerRecordOps.ts; the upsert is gone.)
//
// So this residual never moved through the route mutation catalog, and a lane
// that read "the write is still App's" as "catalog the write" could not have
// shrunk it. A catalog entry's `invalidates` set reaches route RESOURCES only.
// The things the fan-out feeds are neither:
//
//   LayerContext.layerHousings   Historical App state formerly populated by
//                                the loader. Released readers now own their
//                                resources directly; the setter remains a
//                                local compatibility API, not a data owner.
//   triggerMaintenance           Historical App-local fan-out. It is no longer
//                                a route-transition fallback or a source of
//                                global readiness.
//
// ── WHAT RETIRED IT ─────────────────────────────────────────────────────────
//
// The release landed atomically with dropping the prop — not as a partial
// migration of the old readers. The all-data loader and its App-local fan-out
// are now retired; this record surface owns and revalidates its resources, and
// a raw pathname outside its strict contract receives explicit recovery rather
// than repopulating LayerContext. The former fan-out was load-bearing before
// that boundary existed, which is why the halves could not have been split
// across their original change.
//
// ── WHY A PARTIAL CATALOG CONVERSION WAS NOT A SMALLER FIRST STEP ───────────
//
// Cataloguing just the discrete `retireNow` write and leaving the debounced
// `persistHousing` autosave inline does not typecheck as a claim: the
// anti-revert guard in route_mutation_catalog_static is stated per
// (module × table), so declaring a `layer_housings` wave over this module fails
// on the write that stayed. Converting the autosave too is not free either —
// `useRouteMutation` awaits its invalidations, so every autosave tick would
// block on a `layer.record.core` refetch of four tables including the paginated
// daily feeds, where today it updates a local overlay. That is still true, and
// still a separate decision: this release did not convert either write.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'layer.housings.detail',
  obligations: Object.freeze([
    Object.freeze({id: 'layer.record.core', owner: 'permanent'}),
    Object.freeze({id: 'layer.record.appStore', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
